/**
 * ImageMotion usage pricing — credits from clip count, model duration,
 * estimated compose length, and a delivery-margin target.
 *
 * Canonical calculator. The SQL quote RPC and Deno shared copy must stay
 * aligned with this file. Do not change global credit-pack or subscription
 * prices here.
 *
 * Usage pricing stays behind `usagePricingEnabled` until tax, card-fee and
 * FX inputs are supplied. Missing inputs are listed; we do not invent a
 * live customer price.
 */

import { IMAGEMOTION_DOUBLE_CREDIT_OFFERS, IMAGEMOTION_PACKAGES } from './imagemotion';
import { lowestEffectivePaidCreditMinor } from './credit-commerce';
import type { CreditCurrency } from './credit-packs';

export const IMAGEMOTION_DEFAULT_CLIP_SECONDS = 5;
export const IMAGEMOTION_KLING_25_TURBO_PRO_ENDPOINT =
  'fal-ai/kling-video/v2.5-turbo/pro/image-to-video';
export const IMAGEMOTION_KLING_25_TURBO_PRO_USD_PER_5S = 0.35;
export const IMAGEMOTION_SHOTSTACK_PAYG_USD_PER_MINUTE = 0.3;
export const IMAGEMOTION_TARGET_DELIVERY_MARGIN = 0.65;
export const IMAGEMOTION_TITLE_CARD_SECONDS = 3;
export const IMAGEMOTION_DEFAULT_TESTER_CREDIT_ALLOWANCE = IMAGEMOTION_PACKAGES.tester.credits;
export const IMAGEMOTION_DEFAULT_TESTER_COST_BUDGET_USD = 2;

/** Feature flag default. Live charging stays off until settings are complete. */
export const IMAGEMOTION_USAGE_PRICING_DEFAULT_ENABLED = false;

export const IMAGEMOTION_USAGE_PRICING_MISSING_INPUTS = [
  'vat_inclusive',
  'payment_fee_rate',
  'usd_per_gbp',
  'shotstack_plan_confirmed',
] as const;

export type ImagemotionQuoteKind = 'generate' | 'rework' | 'extra_export';

export interface ImagemotionUsageSettings {
  usagePricingEnabled: boolean;
  clipSeconds: number;
  clipCostUsd: number;
  shotstackUsdPerMinute: number;
  shotstackPlan: 'payg_assumed' | 'payg_confirmed' | 'subscription_unknown';
  additionalAllowanceRate: number;
  targetDeliveryMargin: number;
  vatInclusive: boolean | null;
  vatRate: number;
  paymentFeeRate: number | null;
  fxBuffer: number;
  usdPerGbp: number | null;
  testerCreditAllowance: number;
  testerProviderCostBudgetUsd: number;
  maxFallbackAttempts: number;
}

export const IMAGEMOTION_USAGE_SETTINGS_DEFAULTS: ImagemotionUsageSettings = {
  usagePricingEnabled: IMAGEMOTION_USAGE_PRICING_DEFAULT_ENABLED,
  clipSeconds: IMAGEMOTION_DEFAULT_CLIP_SECONDS,
  clipCostUsd: IMAGEMOTION_KLING_25_TURBO_PRO_USD_PER_5S,
  shotstackUsdPerMinute: IMAGEMOTION_SHOTSTACK_PAYG_USD_PER_MINUTE,
  shotstackPlan: 'payg_assumed',
  additionalAllowanceRate: 0,
  targetDeliveryMargin: IMAGEMOTION_TARGET_DELIVERY_MARGIN,
  vatInclusive: null,
  vatRate: 0.2,
  paymentFeeRate: null,
  fxBuffer: 1,
  usdPerGbp: null,
  testerCreditAllowance: IMAGEMOTION_DEFAULT_TESTER_CREDIT_ALLOWANCE,
  testerProviderCostBudgetUsd: IMAGEMOTION_DEFAULT_TESTER_COST_BUDGET_USD,
  maxFallbackAttempts: 0,
};

/** Documented example-only inputs. Not a claim that live fees/FX/tax are known. */
export const IMAGEMOTION_EXAMPLE_REVIEW_SETTINGS: ImagemotionUsageSettings = {
  ...IMAGEMOTION_USAGE_SETTINGS_DEFAULTS,
  usagePricingEnabled: true,
  vatInclusive: false,
  vatRate: 0,
  paymentFeeRate: 0,
  fxBuffer: 1,
  usdPerGbp: 1.27,
  shotstackPlan: 'payg_assumed',
};

export interface ImagemotionUsageQuoteInput {
  generatedClipCount: number;
  clipSeconds?: number;
  includeCompose?: boolean;
  introText?: boolean;
  outroText?: boolean;
  logoIntro?: boolean;
  logoOutro?: boolean;
  logoBumperSeconds?: number;
  voiceoverUsd?: number;
  kind?: ImagemotionQuoteKind;
  tester?: boolean;
  settings?: Partial<ImagemotionUsageSettings>;
}

export interface ImagemotionCurrencyQuote {
  currency: CreditCurrency;
  grossRevenuePerCredit: number;
  netRevenuePerCredit: number | null;
  requiredCredits: number | null;
  estimatedCustomerCharge: number | null;
  estimatedMargin: number | null;
  limitingOffer: string | null;
}

export interface ImagemotionUsageQuote {
  kind: ImagemotionQuoteKind;
  pricingMode: 'usage' | 'legacy_flat' | 'blocked_missing_inputs';
  usagePricingEnabled: boolean;
  missingInputs: string[];
  assumptions: string[];
  generatedClipCount: number;
  clipSeconds: number;
  estimatedFinishedSeconds: number;
  clipCostUsd: number;
  composeCostUsd: number;
  extraServiceCostUsd: number;
  additionalAllowanceUsd: number;
  deliveryCostUsd: number;
  bufferedDeliveryCostUsd: number;
  currencies: Record<CreditCurrency, ImagemotionCurrencyQuote>;
  requiredCredits: number | null;
  chargeable: boolean;
  quoteHash: string;
  tester: boolean;
  testerWithinBudget: boolean;
  modelEndpoint: string;
}

export interface ImagemotionPaidCreditOffer {
  id: string;
  currency: CreditCurrency;
  credits: number;
  amountMinor: number;
  source: 'v2_price_book' | 'imagemotion_offer';
}

function roundUsd(value: number): number {
  return Math.round(value * 10000) / 10000;
}

function mergeSettings(overrides?: Partial<ImagemotionUsageSettings>): ImagemotionUsageSettings {
  return { ...IMAGEMOTION_USAGE_SETTINGS_DEFAULTS, ...overrides };
}

export function estimatedFinishedSeconds(input: {
  generatedClipCount: number;
  clipSeconds?: number;
  introText?: boolean;
  outroText?: boolean;
  logoIntro?: boolean;
  logoOutro?: boolean;
  logoBumperSeconds?: number;
}): number {
  const clips = Math.max(0, Math.floor(input.generatedClipCount));
  const clipSeconds = input.clipSeconds ?? IMAGEMOTION_DEFAULT_CLIP_SECONDS;
  const bumper = input.logoBumperSeconds ?? 3;
  const extras =
    (input.introText ? IMAGEMOTION_TITLE_CARD_SECONDS : 0) +
    (input.outroText ? IMAGEMOTION_TITLE_CARD_SECONDS : 0) +
    (input.logoIntro ? bumper : 0) +
    (input.logoOutro ? bumper : 0);
  return clips * clipSeconds + extras;
}

export function imagemotionPaidCreditOffers(): ImagemotionPaidCreditOffer[] {
  const offers: ImagemotionPaidCreditOffer[] = [];
  for (const currency of ['GBP', 'USD'] as const) {
    const minor = lowestEffectivePaidCreditMinor(currency);
    const credits = 350; // Studio (v3) monthly allowance
    offers.push({
      id: 'studio_v3',
      currency,
      credits,
      amountMinor: minor * credits,
      source: 'v2_price_book',
    });
    for (const offer of Object.values(IMAGEMOTION_DOUBLE_CREDIT_OFFERS)) {
      offers.push({
        id: offer.id,
        currency,
        credits: offer.totalCredits,
        amountMinor: Math.round(offer.prices[currency] * 100),
        source: 'imagemotion_offer',
      });
    }
  }
  return offers;
}

export function lowestPaidCreditMinorForImagemotion(currency: CreditCurrency): {
  minor: number;
  offerId: string;
} {
  let lowest = lowestEffectivePaidCreditMinor(currency);
  let offerId = 'studio_v3';
  for (const offer of Object.values(IMAGEMOTION_DOUBLE_CREDIT_OFFERS)) {
    const minor = Math.round((offer.prices[currency] * 100) / offer.totalCredits);
    if (minor < lowest) {
      lowest = minor;
      offerId = offer.id;
    }
  }
  return { minor: lowest, offerId };
}

function taxNetFactor(settings: ImagemotionUsageSettings): number | null {
  if (settings.vatInclusive === null) return null;
  if (!settings.vatInclusive) return 1;
  const rate = Number(settings.vatRate);
  if (!Number.isFinite(rate) || rate < 0 || rate >= 1) return null;
  return 1 / (1 + rate);
}

function feeFactor(settings: ImagemotionUsageSettings): number | null {
  if (settings.paymentFeeRate === null) return null;
  const rate = Number(settings.paymentFeeRate);
  if (!Number.isFinite(rate) || rate < 0 || rate >= 1) return null;
  return 1 - rate;
}

export function missingUsagePricingInputs(settings: ImagemotionUsageSettings): string[] {
  const missing: string[] = [];
  if (settings.vatInclusive === null) missing.push('vat_inclusive');
  if (settings.paymentFeeRate === null) missing.push('payment_fee_rate');
  if (settings.usdPerGbp === null || !(Number(settings.usdPerGbp) > 0)) missing.push('usd_per_gbp');
  if (settings.shotstackPlan !== 'payg_confirmed') missing.push('shotstack_plan_confirmed');
  return missing;
}

function netRevenuePerCredit(
  currency: CreditCurrency,
  settings: ImagemotionUsageSettings
): { net: number | null; gross: number; offerId: string } {
  const { minor, offerId } = lowestPaidCreditMinorForImagemotion(currency);
  const gross = minor / 100;
  const tax = taxNetFactor(settings);
  const fee = feeFactor(settings);
  if (tax === null || fee === null) {
    return { net: null, gross, offerId };
  }
  return { net: roundUsd(gross * tax * fee), gross, offerId };
}

function requiredCreditsFromCost(
  bufferedCost: number,
  netPerCredit: number | null,
  margin: number
): number | null {
  if (netPerCredit === null || netPerCredit <= 0) return null;
  const keep = 1 - margin;
  if (!(keep > 0)) return null;
  return Math.ceil(bufferedCost / (keep * netPerCredit) - 1e-9);
}

export function quoteHashFromParts(parts: Record<string, unknown>): string {
  const json = JSON.stringify(parts);
  let hash = 2166136261;
  for (let i = 0; i < json.length; i += 1) {
    hash ^= json.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a_${(hash >>> 0).toString(16)}`;
}

export function quoteImagemotionUsage(input: ImagemotionUsageQuoteInput): ImagemotionUsageQuote {
  const settings = mergeSettings(input.settings);
  const kind = input.kind ?? 'generate';
  const generatedClipCount = Math.max(0, Math.floor(input.generatedClipCount));
  const clipSeconds = input.clipSeconds ?? settings.clipSeconds;
  const includeCompose = input.includeCompose !== false && kind !== 'rework';
  const tester = Boolean(input.tester);
  const finishedSeconds = estimatedFinishedSeconds({
    generatedClipCount,
    clipSeconds,
    introText: input.introText,
    outroText: input.outroText,
    logoIntro: input.logoIntro,
    logoOutro: input.logoOutro,
    logoBumperSeconds: input.logoBumperSeconds,
  });

  const clipCostUsd = roundUsd(generatedClipCount * settings.clipCostUsd);
  const composeCostUsd = includeCompose
    ? roundUsd((finishedSeconds / 60) * settings.shotstackUsdPerMinute)
    : 0;
  const extraServiceCostUsd = roundUsd(Math.max(0, Number(input.voiceoverUsd) || 0));
  const rawDelivery = roundUsd(clipCostUsd + composeCostUsd + extraServiceCostUsd);
  const additionalAllowanceUsd = roundUsd(rawDelivery * Math.max(0, settings.additionalAllowanceRate));
  const deliveryCostUsd = roundUsd(rawDelivery + additionalAllowanceUsd);
  const fxBuffer = settings.fxBuffer > 0 ? settings.fxBuffer : 1;
  const bufferedDeliveryCostUsd = roundUsd(deliveryCostUsd * fxBuffer);

  const missing = missingUsagePricingInputs(settings);
  const assumptions = [
    `Kling 2.5 Turbo Pro is ${settings.clipCostUsd.toFixed(2)} USD per ${settings.clipSeconds}s clip.`,
    `Shotstack ${settings.shotstackPlan} at ${settings.shotstackUsdPerMinute.toFixed(2)} USD per finished minute.`,
    `Target delivery margin is ${(settings.targetDeliveryMargin * 100).toFixed(0)}% — a proposal, not proven profitability.`,
    'Welcome / tester credits are treated as acquisition cost, not paid revenue.',
    settings.vatInclusive === null
      ? 'VAT treatment is unknown — net revenue per credit is not final.'
      : settings.vatInclusive
        ? `Catalogue prices treated as VAT-inclusive at ${(settings.vatRate * 100).toFixed(0)}%.`
        : 'Catalogue prices treated as net of VAT.',
    settings.paymentFeeRate === null
      ? 'Card / payment fees are unknown.'
      : `Payment fee rate assumed at ${(settings.paymentFeeRate * 100).toFixed(2)}%.`,
    settings.usdPerGbp === null
      ? 'GBP/USD conversion is unknown.'
      : `USD per GBP assumed at ${settings.usdPerGbp} with FX buffer ${fxBuffer}.`,
  ];

  const currencies = {} as Record<CreditCurrency, ImagemotionCurrencyQuote>;
  for (const currency of ['GBP', 'USD'] as const) {
    const { net, gross, offerId } = netRevenuePerCredit(currency, settings);
    let costInCurrency = bufferedDeliveryCostUsd;
    if (currency === 'GBP') {
      if (settings.usdPerGbp && settings.usdPerGbp > 0) {
        costInCurrency = roundUsd(bufferedDeliveryCostUsd / settings.usdPerGbp);
      } else {
        costInCurrency = bufferedDeliveryCostUsd;
      }
    }
    const required = requiredCreditsFromCost(costInCurrency, net, settings.targetDeliveryMargin);
    currencies[currency] = {
      currency,
      grossRevenuePerCredit: gross,
      netRevenuePerCredit: net,
      requiredCredits: required,
      estimatedCustomerCharge: required !== null ? roundUsd(required * gross) : null,
      estimatedMargin:
        required !== null && net !== null && required * net > 0
          ? roundUsd(1 - costInCurrency / (required * net))
          : null,
      limitingOffer: offerId,
    };
  }

  const computedCredits =
    currencies.GBP.requiredCredits !== null && currencies.USD.requiredCredits !== null
      ? Math.max(currencies.GBP.requiredCredits, currencies.USD.requiredCredits)
      : null;
  const usageReady = missing.length === 0 && settings.usagePricingEnabled;
  const requiredCredits = tester ? settings.testerCreditAllowance : computedCredits;

  const testerWithinBudget =
    !tester || deliveryCostUsd <= settings.testerProviderCostBudgetUsd + 1e-9;

  const pricingMode: ImagemotionUsageQuote['pricingMode'] = usageReady
    ? 'usage'
    : settings.usagePricingEnabled
      ? 'blocked_missing_inputs'
      : 'legacy_flat';

  return {
    kind,
    pricingMode,
    usagePricingEnabled: settings.usagePricingEnabled,
    missingInputs: missing,
    assumptions,
    generatedClipCount,
    clipSeconds,
    estimatedFinishedSeconds: finishedSeconds,
    clipCostUsd,
    composeCostUsd,
    extraServiceCostUsd,
    additionalAllowanceUsd,
    deliveryCostUsd,
    bufferedDeliveryCostUsd,
    currencies,
    requiredCredits: tester && pricingMode === 'usage' ? settings.testerCreditAllowance : requiredCredits,
    chargeable: pricingMode === 'usage' && requiredCredits !== null && (tester ? testerWithinBudget : true),
    quoteHash: quoteHashFromParts({
      kind,
      generatedClipCount,
      clipSeconds,
      finishedSeconds,
      clipCostUsd,
      composeCostUsd,
      extraServiceCostUsd,
      bufferedDeliveryCostUsd,
      margin: settings.targetDeliveryMargin,
    }),
    tester,
    testerWithinBudget,
    modelEndpoint: IMAGEMOTION_KLING_25_TURBO_PRO_ENDPOINT,
  };
}

export function exampleUsageQuotes(
  imageCounts: number[] = [5, 10, 15, 25],
  settings: ImagemotionUsageSettings = IMAGEMOTION_EXAMPLE_REVIEW_SETTINGS
): ImagemotionUsageQuote[] {
  return imageCounts.map((n) =>
    quoteImagemotionUsage({
      generatedClipCount: n,
      includeCompose: true,
      settings,
    })
  );
}

export function formatUsageQuoteSummary(quote: ImagemotionUsageQuote): string {
  const credits =
    quote.requiredCredits == null ? 'not priced' : `${quote.requiredCredits} credits`;
  return `${quote.generatedClipCount} clips × ${quote.clipSeconds}s → ~${quote.estimatedFinishedSeconds}s · ${credits}`;
}

export interface ClipGenerationTarget {
  id: string;
  clip_url?: string | null;
}

/** Images that still need a fal generation. Existing clip URLs are reused. */
export function clipsRequiringGeneration(
  images: ClipGenerationTarget[],
  inflightImageIds: Iterable<string> = []
): ClipGenerationTarget[] {
  const inflight = new Set(inflightImageIds);
  return images.filter((image) => !image.clip_url && !inflight.has(image.id));
}

export function remainingBalanceAfter(balance: number, credits: number): number {
  return Math.round((Number(balance) - Number(credits)) * 100) / 100;
}

export function isMoreExpensiveModel(
  candidateCostUsd: number | null | undefined,
  primaryCostUsd: number | null | undefined
): boolean {
  const candidate = Number(candidateCostUsd);
  const primary = Number(primaryCostUsd);
  if (!Number.isFinite(candidate) || !Number.isFinite(primary)) return false;
  return candidate > primary + 1e-9;
}

export type UsageChargeError =
  | 'INSUFFICIENT_CREDITS'
  | 'PRICE_CHANGED'
  | 'NOT_CHARGEABLE'
  | 'TESTER_BUDGET_EXCEEDED';

export type UsageChargeDecision =
  | { ok: true; alreadyCharged?: boolean }
  | { ok: false; error: UsageChargeError; requiredCredits?: number | null };

/** Server-side accept/reject of a usage quote before reserving credits. */
export function evaluateUsageCharge(input: {
  balance: number;
  acceptedCredits: number;
  quotedCredits: number | null;
  chargeable: boolean;
  testerWithinBudget?: boolean;
  alreadyCharged?: boolean;
  existingIdempotencyKey?: string | null;
  idempotencyKey: string;
}): UsageChargeDecision {
  if (
    input.alreadyCharged ||
    (input.existingIdempotencyKey != null &&
      input.existingIdempotencyKey === input.idempotencyKey)
  ) {
    return { ok: true, alreadyCharged: true };
  }
  if (input.testerWithinBudget === false) {
    return { ok: false, error: 'TESTER_BUDGET_EXCEEDED' };
  }
  if (!input.chargeable || input.quotedCredits == null) {
    return { ok: false, error: 'NOT_CHARGEABLE', requiredCredits: input.quotedCredits };
  }
  if (input.acceptedCredits !== input.quotedCredits) {
    return {
      ok: false,
      error: 'PRICE_CHANGED',
      requiredCredits: input.quotedCredits,
    };
  }
  if (input.balance < input.acceptedCredits) {
    return { ok: false, error: 'INSUFFICIENT_CREDITS', requiredCredits: input.quotedCredits };
  }
  return { ok: true };
}
